/**
 * FishSellCard
 * Modular card component for selling a single fish stack.
 * Based on the QuestSellPanel pattern: quantity input + "Sell N" + "Sell All" per stack.
 *
 * Used by both MarketSellView and QuestSellPanel for a consistent selling experience.
 */

import { useState } from 'react';
import { getFishDefinition, formatCurrency } from '@/data';
import { sellManager } from '@/systems/selling/SellManager';
import { FishStack, Quality } from '@/types';
import { QualityColors, QualityNames, QualityMultipliers } from '@/types/enums';

export interface FishSellCardProps {
  /** The fish stack to display */
  stack: FishStack;
  /** Buyer/market ID for value calculations */
  buyerId: string;
  /** Maximum number of fish that can be sold from this stack (e.g. quest cap or stack.count) */
  maxSellable: number;
  /** Callback when fish are sold */
  onSell: (definitionId: string, quality: Quality, count: number) => void;
  /** Whether this stack is protected from bulk sell */
  isProtected?: boolean;
  /** Callback to toggle protection lock */
  onToggleProtection?: () => void;
}

export function FishSellCard({
  stack,
  buyerId,
  maxSellable,
  onSell,
  isProtected = false,
  onToggleProtection,
}: FishSellCardProps) {
  const [quantity, setQuantity] = useState(1);

  const fishDef = getFishDefinition(stack.definitionId);
  const effectiveMax = Math.min(stack.count, maxSellable);
  const canSell = effectiveMax > 0 && !isProtected;

  // Always calculate values so both buttons can display them
  const perFishValue = sellManager.calculateValue({ ...stack, count: 1 }, buyerId);
  const sellQty = Math.min(quantity, effectiveMax);
  const sellNValue = perFishValue * sellQty;
  const sellAllValue = perFishValue * effectiveMax;

  const handleSell = () => {
    const sellCount = Math.min(quantity, effectiveMax);
    if (sellCount > 0) {
      onSell(stack.definitionId, stack.quality, sellCount);
    }
  };

  const handleSellAll = () => {
    if (effectiveMax > 0) {
      onSell(stack.definitionId, stack.quality, effectiveMax);
    }
  };

  return (
    <div
      className={`bg-gray-800 rounded-lg border-2 p-4 transition-all ${
        isProtected
          ? 'border-yellow-600 bg-yellow-900/20'
          : 'border-gray-700'
      }`}
    >
      {/* Species and Quality Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-lg">{fishDef.emoji || fishDef.icon}</span>
            <div>
              <div className="font-bold text-lg">{fishDef.name}</div>
              <div
                className="text-sm font-semibold"
                style={{ color: QualityColors[stack.quality] }}
              >
                {QualityNames[stack.quality]}
              </div>
            </div>
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {stack.count} available
            <span className="text-green-400 ml-2">
              +{Math.round((QualityMultipliers[stack.quality] - 1) * 100)}% bonus
            </span>
          </div>
        </div>

        {/* Protection Lock Toggle */}
        {onToggleProtection && (
          <button
            onClick={onToggleProtection}
            className={`px-3 py-2 rounded-lg font-bold transition-all text-sm ${
              isProtected
                ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
            }`}
            title={
              isProtected
                ? 'Unlock (allow selling)'
                : 'Lock (protect from bulk sell)'
            }
          >
            {isProtected ? '🔒 Locked' : '🔓'}
          </button>
        )}
      </div>

      {/* Value Preview */}
      {!isProtected && effectiveMax > 0 && (
        <div className="text-xs text-gray-400 mb-3">
          {formatCurrency(perFishValue)}/fish
          {effectiveMax > 1 && (
            <span className="text-gray-500 ml-2">
              (all: {formatCurrency(sellAllValue)})
            </span>
          )}
        </div>
      )}

      {/* Sell Controls */}
      {canSell && (
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 flex-1">
            <label className="text-sm text-gray-400 whitespace-nowrap">Sell:</label>
            <input
              type="number"
              min="1"
              max={effectiveMax}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-20 bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-center focus:outline-none focus:border-blue-500"
            />
            <button
              onClick={handleSell}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-bold transition-colors"
            >
              Sell {sellQty}
              {sellNValue > 0 && (
                <span className="text-green-200 ml-1 font-normal text-sm">
                  ({formatCurrency(sellNValue)})
                </span>
              )}
            </button>
          </div>

          <button
            onClick={handleSellAll}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded font-bold transition-colors whitespace-nowrap"
          >
            Sell All
            {sellAllValue > 0 && (
              <span className="text-emerald-200 ml-1 font-normal text-sm">
                ({formatCurrency(sellAllValue)})
              </span>
            )}
          </button>
        </div>
      )}

      {/* Protected state */}
      {isProtected && (
        <div className="text-yellow-500 text-sm font-bold text-center py-2">
          Protected from selling
        </div>
      )}

      {/* Nothing to sell */}
      {!isProtected && effectiveMax <= 0 && (
        <div className="text-gray-500 text-sm text-center py-2">
          Nothing to sell
        </div>
      )}
    </div>
  );
}
